import React from "react";
import { Eraser, Map, MousePointer2, Save, SquareDashedMousePointer } from "lucide-react";
import MapView from "@/components/MapView";

const CITY_CENTERS = {
    milano: [45.4642, 9.19], roma: [41.9028, 12.4964], torino: [45.0703, 7.6869], napoli: [40.8518, 14.2681],
    bologna: [44.4949, 11.3426], firenze: [43.7696, 11.2558], genova: [44.4056, 8.9463], venezia: [45.4408, 12.3155],
    verona: [45.4384, 10.9916], bari: [41.1171, 16.8719], palermo: [38.1157, 13.3615], catania: [37.5079, 15.083],
    cagliari: [39.2238, 9.1217], trieste: [45.6495, 13.7768], padova: [45.4064, 11.8768], savona: [44.3075, 8.481],
};

const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
const hash = (value) => [...String(value)].reduce((total, char) => ((total * 31) + char.charCodeAt(0)) >>> 0, 2166136261);
const nextRandom = (seed) => ((seed * 1664525 + 1013904223) >>> 0);

function pointInPolygon([lat, lng], polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [yi, xi] = polygon[i];
        const [yj, xj] = polygon[j];
        const intersects = ((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi);
        if (intersects) inside = !inside;
    }
    return inside;
}

function approximatePoint(item, index, polygon, center) {
    let seed = hash(`${item.id}-${index}`);
    if (polygon?.length >= 3) {
        const lats = polygon.map((point) => point[0]);
        const lngs = polygon.map((point) => point[1]);
        const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
        for (let attempt = 0; attempt < 24; attempt += 1) {
            seed = nextRandom(seed); const lat = minLat + (seed / 4294967295) * (maxLat - minLat);
            seed = nextRandom(seed); const lng = minLng + (seed / 4294967295) * (maxLng - minLng);
            if (pointInPolygon([lat, lng], polygon)) return [lat, lng];
        }
    }
    return center;
}

export default function AcquisitionMap({ filters, results, polygon, onPolygonChange, drawing, onDrawingChange, onSave, saveDisabled, focusKey, savedViewport, citySelection, onViewportChange }) {
    const cityCenter = citySelection ? [citySelection.lat, citySelection.lng] : CITY_CENTERS[normalize(filters.city)];
    const polygonCenter = polygon?.length ? [
        polygon.reduce((sum, point) => sum + point[0], 0) / polygon.length,
        polygon.reduce((sum, point) => sum + point[1], 0) / polygon.length,
    ] : null;
    const center = savedViewport?.center || polygonCenter || cityCenter || [42.8, 12.5];
    const zoom = savedViewport?.zoom || (citySelection ? citySelection.zoom : (cityCenter || polygonCenter ? 12 : 6));
    const markers = (results?.items || []).map((item, index) => {
        const [latitude, longitude] = approximatePoint(item, index, polygon, center);
        return {
            ...item, latitude, longitude, transaction_type: filters.operation,
            surface_sqm: item.sqm, photos: item.image ? [item.image] : [], external_url: item.url,
            city: filters.city || filters.province || filters.region,
        };
    });

    const dispatchMapEvent = (name) => document.querySelector('[data-testid="acq-zone-map"]')?.dispatchEvent(new CustomEvent(name));
    const clear = () => { dispatchMapEvent("vd:clear-draft"); onDrawingChange(false); onPolygonChange(null); };

    return (
        <section data-testid="acq-map-panel" className="acq-map-panel-stack relative isolate z-0 min-w-0 rounded-lg bg-white p-3 md:p-4 shadow-[10px_10px_24px_#c9d5e3,-10px_-10px_24px_#ffffff] border border-white">
            <div className="relative z-[2] flex items-center justify-between gap-3 flex-wrap px-1 pb-3">
                <div>
                    <div className="text-xs font-bold uppercase text-blue-700 flex items-center gap-2"><Map size={15} /> Mappa area di acquisizione</div>
                    <p className="text-xs text-slate-500 mt-1">Disegna un poligono; i marker indicano la zona approssimativa degli annunci pubblici.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button data-testid="acq-map-draw" type="button" onClick={() => onDrawingChange(!drawing)} className={`px-3 py-2 text-xs font-semibold rounded-md inline-flex items-center gap-2 transition-colors ${drawing ? "bg-blue-100 text-blue-800 border border-blue-300" : "bg-slate-100 text-slate-700 border border-slate-200 hover:bg-blue-50"}`}>
                        <SquareDashedMousePointer size={14} /> {drawing ? "Disegno attivo" : "Disegna poligono"}
                    </button>
                    {drawing && <button data-testid="acq-map-finish" type="button" onClick={() => dispatchMapEvent("vd:finish-polygon")} className="px-3 py-2 text-xs font-semibold rounded-md bg-blue-600 text-white inline-flex items-center gap-2 hover:bg-blue-700"><MousePointer2 size={14} /> Termina</button>}
                    <button data-testid="acq-map-clear" type="button" onClick={clear} disabled={!polygon && !drawing} className="p-2 rounded-md bg-slate-100 text-slate-500 border border-slate-200 hover:text-red-600 disabled:opacity-40" title="Cancella poligono"><Eraser size={15} /></button>
                    <button data-testid="acq-map-save" type="button" onClick={onSave} disabled={saveDisabled || !polygon} className="px-4 py-2 text-xs font-bold rounded-md bg-blue-600 text-white inline-flex items-center gap-2 shadow-[4px_4px_10px_#b8c5d5,-3px_-3px_8px_#ffffff] hover:bg-blue-700 disabled:opacity-40 disabled:shadow-none">
                        <Save size={14} /> Salva questa zona
                    </button>
                </div>
            </div>
            <div className="acq-zone-map relative isolate z-0 h-[clamp(520px,68vh,760px)] min-h-[520px] overflow-hidden rounded-md border border-blue-100 shadow-[inset_3px_3px_10px_#cbd8e6,inset_-3px_-3px_10px_#ffffff]">
                <MapView
                    properties={markers}
                    center={center}
                    zoom={zoom}
                    drawingEnabled={drawing}
                    polygon={polygon}
                    onPolygonComplete={(coords) => { onPolygonChange(coords); onDrawingChange(false); }}
                    onViewportChange={onViewportChange}
                    theme="light"
                    polygonColor="#2563eb"
                    focusKey={focusKey}
                    testId="acq-zone-map"
                />
            </div>
            <div data-testid="acq-map-marker-count" className="px-1 pt-3 text-xs text-slate-500">{markers.length} marker · posizioni indicative nel perimetro selezionato</div>
        </section>
    );
}